import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {DxDataGridModule, DxFormModule, DxTemplateModule} from 'devextreme-angular';
import {DxDataGridTypes} from 'devextreme-angular/ui/data-grid';
import {GridPageComponent, FilterConfig} from "../../../common/grid-page.component";
import {MissedOpportunity, OrderException, OrderLineDto, OrderWmStatus, PreProductionStatus} from "@services";
import {FilterPanelComponent} from "../../../../components/library/filter-panel/filter-panel.component";
import EnumUtils from "../../../../shared/enum-utils";
import {PreProductionStatusPipe} from "../../../../pipes/pre-production-status.pipe";
import {OrderExceptionPipe} from "../../../../pipes/order-exception.pipe";
import {MissedOpportunityPipe} from "../../../../pipes/missed-opportunity.pipe";
import {DATE_TIME_FORMAT} from "../../../../shared/constants";

/**
 * Reusable PO line-level grid. It owns the single source of truth for the order-line column
 * configuration (available columns, default visibility, order, formatting, filtering, sorting and
 * data types) so that every view that shows order lines — the PO Line Level tab and the Shipment
 * Line Items view — stays aligned automatically. New columns only need to be added here.
 */
@Component({
    selector: 'app-order-lines-grid',
    standalone: true,
    imports: [
        CommonModule,
        DxDataGridModule,
        DxFormModule,
        DxTemplateModule,
        FilterPanelComponent,
        RouterLink,
        PreProductionStatusPipe,
        OrderExceptionPipe,
        MissedOpportunityPipe
    ],
    providers: [PreProductionStatusPipe, OrderExceptionPipe, MissedOpportunityPipe],
    templateUrl: './order-lines-grid.component.html'
})
export class OrderLinesGridComponent extends GridPageComponent<OrderLineDto> {
    private router = inject(Router);
    private preProductionStatusPipe = inject(PreProductionStatusPipe);
    private orderExceptionPipe = inject(OrderExceptionPipe);
    private missedOpportunityPipe = inject(MissedOpportunityPipe);

    @Input({required: true}) dataSource: any;
    @Input() gridTitle = 'PO Line Level';
    @Input() gridId = 'orderLinesLevelGrid';
    @Input() storageKey = 'orders-line-level-grid-state';
    @Input() gridHeight = 'calc(100vh - 140px)';
    @Input() showBackButton = false;
    @Input() set exportFileName(value: string) { this.exportName = value; }
    @Output() backClick = new EventEmitter<void>();

    dateTimeFormat = DATE_TIME_FORMAT;
    lineKey = ['salesDocument', 'salesDocumentItem', 'productionItem'];
    statusOptions = EnumUtils.getEnumOptions(OrderWmStatus);
    preProductionStatusOptions = EnumUtils.getEnumOptions(PreProductionStatus, (v) =>
        this.preProductionStatusPipe.transform(v),
    );

    exceptionHeaderFilter = {
        dataSource: EnumUtils.getEnumOptions(OrderException).map(({value}) => ({
            text: this.orderExceptionPipe.transform(value),
            value: [this.gridFieldName('exceptions'), 'contains', value],
        })),
    };

    missedOpportunityHeaderFilter = {
        dataSource: EnumUtils.getEnumOptions(MissedOpportunity).map(({value}) => ({
            text: this.missedOpportunityPipe.transform(value),
            value: [this.gridFieldName('missedOpportunities'), 'contains', value],
        })),
    };

    protected override exportName: string = "PO Line Level";

    protected override filterConfig: FilterConfig[] = [
        {field: 'salesDocument', op: 'contains'},
        {field: 'material', op: 'contains'},
        {field: 'materialDescription', op: 'contains'},
        {field: 'deliveryNumber', op: 'contains'},
        {field: 'logisticalGroup', op: 'contains'},
        {field: 'wmActivityStatus', op: '='},
        {field: 'preProductionStatus', op: '='}
    ];

    constructor() {
        super();
        this.init(OrderLineDto);
    }

    back = (): void => this.backClick.emit();

    override async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const {data} = e;
        await this.router.navigate([`/outbound/orders/details/${data.salesDocument}`]);
    }

    refreshGrid = async () => {
        await this.dataGrid.instance.refresh();
    };

    customizeSum = (data: any) => `Total: ${data.value}`;

    calculateExceptionsCellValue = (line: OrderLineDto): string =>
        (line.exceptions ?? []).map(exception => this.orderExceptionPipe.transform(exception)).join(', ');

    calculateMissedOpportunityCellValue = (line: OrderLineDto): string =>
        (line.missedOpportunities ?? []).map(opportunity => this.missedOpportunityPipe.transform(opportunity)).join(', ');

    protected getPreProductionBadgeClass(status: PreProductionStatus): string {
        return EnumUtils.getPreProductionStatusBadgeClass(status);
    }
}
